/**
 * useAuth hook - provides auth state and actions to React components.
 * Separated from components to comply with react-refresh rules.
 */

import { useCallback, useEffect, useState } from 'react'
import type { AuthUser } from '@provendoro/shared/types'
import type { RegisterInput, LoginInput } from '@provendoro/shared/validation'
import * as authClient from './auth-client.js'

export interface UseAuthReturn {
  user: AuthUser | null
  loading: boolean
  error: string | null
  login: (input: LoginInput) => Promise<boolean>
  register: (input: RegisterInput) => Promise<boolean>
  logout: () => Promise<void>
  clearError: () => void
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check session on mount
  useEffect(() => {
    let cancelled = false
    authClient.getSessionUser().then((result) => {
      if (cancelled) return
      if (result.data?.user) {
        setUser(result.data.user)
      }
      setLoading(false)
    }).catch(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  const login = useCallback(async (input: LoginInput): Promise<boolean> => {
    setError(null)
    setLoading(true)
    try {
      const result = await authClient.signIn(input)
      if (result.error) {
        setError(result.error.message)
        setLoading(false)
        return false
      }
      if (result.data?.user) {
        setUser(result.data.user)
      }
      setLoading(false)
      return true
    } catch {
      setError('Network error. Please try again.')
      setLoading(false)
      return false
    }
  }, [])

  const register = useCallback(async (input: RegisterInput): Promise<boolean> => {
    setError(null)
    setLoading(true)
    try {
      const result = await authClient.signUp(input)
      if (result.error) {
        setError(result.error.message)
        setLoading(false)
        return false
      }
      if (result.data?.user) {
        setUser(result.data.user)
      }
      setLoading(false)
      return true
    } catch {
      setError('Network error. Please try again.')
      setLoading(false)
      return false
    }
  }, [])

  const logout = useCallback(async () => {
    await authClient.signOut()
    setUser(null)
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return { user, loading, error, login, register, logout, clearError }
}
